import { ArrowDownTrayIcon, ChevronLeftIcon, TrashIcon } from '@heroicons/react/24/outline'
import { setGlobalConfig } from 'actions/global.config.action'
import { GlobalConfigType } from 'actions/global.config.type'
import { trackHmdaPipeRun } from 'components/BackgroundRuns'
import { LayoutLoading } from 'components/LayoutLoading'
import { Sortable } from 'components/Sortable'
import {
  accountTypes,
  channelTypeOptions,
  defaultRateFilterOptions,
  holdFilterOptions,
  loanOptionsConstants,
  loanStatusConstants,
  lockTypeOptions,
} from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { useProductTypeOptions } from 'hooks/useProductTypeOptions'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
  downloadHmdaPipeJobFile,
  downloadReports,
  downloadReportsCSV,
  generateReportsPreview,
  getAdminConfig,
  getAdminToAeEmails,
  getHmdaPipeJob,
  getLoanReport,
  getLoanReportsPlaceholders,
  getLoanTagSuggestions,
  saveReports,
  startHmdaPipeJob,
} from 'services'
import { svgLoading, svgSearch } from 'stories/assets'
import {
  Button,
  Checkbox,
  HorzScrollContainer,
  Input2,
  InputDateRange,
  Modal,
  MultiSelect,
  Select2,
  Tooltip,
} from 'stories/components'
import { filterObject, generateOptions } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { ReportPlaceholders } from './ReportPlaceholders'
import { ActionType, DateFilterPayload, getDateHeaders, IReportHeaders, LoanReportsOrigin } from './types'

const extractHeaderLabel = (header: string) => {
  const trimmed = header.trim()
  if (trimmed.includes(' - ')) return trimmed.split(' - ').pop()!.trim()
  if (trimmed.includes('-')) return trimmed.split('-').pop()!.trim()
  return trimmed
}

const hasPreviewCellValue = (value: any) => {
  if (value === null || value === undefined) return false
  const text = String(value).trim()
  return text !== '' && text !== '-'
}

const actionApiMap = {
  preview: generateReportsPreview,
  download: downloadReports,
  downloadCSV: downloadReportsCSV,
} as const

/** Codes the HMDA conversion writes into the Loan Purpose column. */
const defaultHmdaLoanPurposeCodes: Record<string, string> = {
  'Home Purchase': '1',
  'Home Improvement': '2',
  Refinancing: '31',
  'Cash-out Refinancing': '32',
  Other: '4',
  'Not Applicable': '5',
}

const hmdaPurposeOptionsFromCodes = (codes: Record<string, string>) => {
  const options: Record<string, string> = {}
  Object.entries(codes || {}).forEach(([label, code]) => {
    if (code === undefined || code === null || String(code) === '') return
    options[String(code)] = `${code} - ${label}`
  })
  return options
}

const ReportDetails = () => {
  const { id } = useParams() as any
  const navigate = useHistory()
  const location = useLocation()
  const queryOrigin = useMemo(() => {
    const params = new URLSearchParams(location.search)
    const value = params.get('origin')
    if (value === null) return null
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }, [location.search])

  const isNew = Number(id) == 0
  const isStandard = Number(id) != id
  const dispatch = useDispatch()
  const viewerKey = useSelector(
    (state: any) =>
      `${state.auth?.profile?.id || ''}|${state.auth?.profile?.email || ''}|${state.auth?.role?.impersonatedBy || ''}`,
  )
  const [loading, setLoading] = useState(false)
  const cachedPlaceholders = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.ReportPlaceholders],
  ) as Record<string, IReportHeaders[]>
  const [placeholders, setPlaceholders] = useState<Record<string, IReportHeaders[]>>(
    cachedPlaceholders?.Loan ? cachedPlaceholders : { Loan: [] },
  )
  const [type, setType] = useState('Loan')
  const [selectedPlaces, setSelectedPlaces] = useState<string[]>(['Loan #'])
  const [title, setTitle] = useState('')
  const { hasPermission } = usePermissions()
  const canConfigureAccess = hasPermission('Reports.Access')
  const [canEdit, setCanEdit] = useState(isNew)
  const [userEmails, setUserEmails] = useState<string[]>([])
  const [access, setAccess] = useState<Record<string, Record<string, boolean>>>({
    viewAccountTypes: {},
    viewUsers: {},
    editAccountTypes: {},
    editUsers: {},
  })

  const userOptions = useMemo(() => {
    const options: Record<string, string> = {}
    userEmails.forEach((email) => (options[email] = email))
    return options
  }, [userEmails])

  const [loanGlobalStatus, loanSubStatusConfig] = useSelector((state: any) => [
    state.loanGlobalStatus,
    state.globalConfigReducer[GlobalConfigType.LoanSubStatus] as Record<string, { id: string; title: string }[]>,
  ])

  const fullLoanStatusOptions = useMemo(
    () => ({ ...loanGlobalStatus, ...loanStatusConstants.servicingStatus }),
    [loanGlobalStatus],
  )

  const subStatusOptions = useMemo(() => {
    const options: Record<string, string> = {}
    Object.keys(loanSubStatusConfig || {}).forEach((statusKey) => {
      ;(loanSubStatusConfig[statusKey] || []).forEach((sub: { id: string; title: string }) => {
        if (!sub?.id) return
        const statusLabel = loanGlobalStatus[statusKey] || statusKey
        options[`${statusKey}:${sub.id}`] = `${statusLabel} - ${sub.title}`
      })
    })
    return options
  }, [loanSubStatusConfig, loanGlobalStatus])

  const hasSubStatusOptions = Object.keys(subStatusOptions).length > 0
  const { optionsMap: productTypeOptionsMap } = useProductTypeOptions()

  const [origin, setOrigin] = useState<LoanReportsOrigin>(
    (queryOrigin as LoanReportsOrigin) ?? LoanReportsOrigin.Origination,
  )

  const [filters, setFilters] = useState<Record<string, any>>({
    query: '',
    status: generateOptions(fullLoanStatusOptions),
    statusMode: 'status',
    subStatus: {},
    lock: generateOptions(lockTypeOptions),
    state: generateOptions(loanOptionsConstants.states),
    type: generateOptions(channelTypeOptions),
    productType: generateOptions(productTypeOptionsMap),
    propertyType: generateOptions(loanOptionsConstants.propertyType),
    tags: {},
    excludeTags: {},
    asOf: {},
    lastStatusChange: {},
    defaultRate: '0',
    hold: '0',
    hmdaLoanPurpose: {},
    orderBy: 'asOf',
    orderDir: '-1',
    skip: 0,
    count: 20,
  })
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      productType: {
        ...generateOptions(productTypeOptionsMap, true),
        ...prev.productType,
      },
    }))
  }, [productTypeOptionsMap])

  const [preview, setPreview] = useState<string[][]>([])
  const [tagOptions, setTagOptions] = useState<Record<string, string>>({})
  const [total, setTotal] = useState(0)
  // Loans the date-range scan got through, when the report was too big to check all of them.
  const [dateFilterScanLimit, setDateFilterScanLimit] = useState(0)
  const [queryTimes, setQueryTimes] = useState(0)
  const [isDateModalOpen, setIsDateModalOpen] = useState(false)
  const [dateModalColumns, setDateModalColumns] = useState<string[]>([])
  const [dateFilters, setDateFilters] = useState<DateFilterPayload[]>([{ column: '', range: {} }])
  const [pendingAction, setPendingAction] = useState<ActionType | null>(null)
  const [dateModalError, setDateModalError] = useState('')
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [requiredHeaders, setRequiredHeaders] = useState<string[]>([])
  const [isRequiredModalOpen, setIsRequiredModalOpen] = useState(false)
  const [requiredModalHeader, setRequiredModalHeader] = useState('')
  const [requiredModalChecked, setRequiredModalChecked] = useState(false)
  const [hmdaPurposeOptions, setHmdaPurposeOptions] = useState<Record<string, string>>(
    hmdaPurposeOptionsFromCodes(defaultHmdaLoanPurposeCodes),
  )

  const resolveDisplayName = useMemo(() => {
    const customTitleMap: Record<string, string> = {}
    const itemLabelMap: Record<string, Record<string, string>> = {}

    for (const header of placeholders.Loan || []) {
      if (header.customTitles) {
        for (const [key, label] of Object.entries(header.customTitles)) {
          customTitleMap[key] = label
        }
      }
      if (header.items) {
        for (const dataKey of header.data) {
          itemLabelMap[dataKey] = header.items
        }
      }
      if (header.subItems) {
        for (const [dataKey, items] of Object.entries(header.subItems)) {
          itemLabelMap[dataKey] = items
        }
      }
    }

    return (raw: string): string => {
      const dashIdx = raw.indexOf('-')
      if (dashIdx === -1) return customTitleMap[raw] || raw
      const name = raw.substring(0, dashIdx)
      const itemKey = raw.substring(dashIdx + 1)
      const displayName = customTitleMap[name] || name
      const displayItem = itemLabelMap[name]?.[itemKey] || itemKey
      return `${displayName} - ${displayItem}`
    }
  }, [placeholders])

  const dateHeadersSet = useMemo(() => new Set(getDateHeaders(placeholders)), [placeholders])

  const filterOptionSizes = useMemo(
    () => ({
      status: Object.keys(generateOptions(fullLoanStatusOptions)).length,
      lock: Object.keys(generateOptions(lockTypeOptions)).length,
      type: Object.keys(generateOptions(channelTypeOptions)).length,
      productType: Object.keys(generateOptions(productTypeOptionsMap)).length,
      propertyType: Object.keys(generateOptions(loanOptionsConstants.propertyType)).length,
      state: Object.keys(generateOptions(loanOptionsConstants.states)).length,
    }),
    [fullLoanStatusOptions, productTypeOptionsMap],
  )

  // Load the tag universe for the tag filter (all tags used across loans).
  useEffect(() => {
    getLoanTagSuggestions('')
      .then((tags) => setTagOptions(Object.fromEntries((Array.isArray(tags) ? tags : []).map((t) => [t, t]))))
      .catch(() => {})
  }, [])

  useEffect(() => {
    const initData = async () => {
      setLoading(true)

      const data = await getLoanReportsPlaceholders()
      dispatch(setGlobalConfig(GlobalConfigType.ReportPlaceholders, data))
      setPlaceholders(data)

      if (isNew || isStandard) {
        if (id === 'hmda') {
          // The standard HMDA Report exposes the 106 placeholders defined
          // in the admin HMDA Conversion config (the Excel Loan Input
          // sheet order, minus the 4 filler "Misc1" columns).
          try {
            const { fields } = await getAdminConfig('hmdaConversion')
            const hmdaPlaceholders = (fields || []).map((f: any) => f.placeholder)
            const purposeField = (fields || []).find((field: any) => field.placeholder === 'Loan Purpose')
            if (purposeField?.codes) setHmdaPurposeOptions(hmdaPurposeOptionsFromCodes(purposeField.codes))
            setPlaceholders({
              Loan: [{ title: 'HMDA Report', data: hmdaPlaceholders }],
            })
            setSelectedPlaces(hmdaPlaceholders)
          } catch {
            const hmdaSection = data.Loan.find((item: any) => item.title === 'HMDA')
            setPlaceholders({
              Loan: [data.Loan[0], ...(hmdaSection ? [hmdaSection] : [])],
            })
          }
        }
        setLoading(false)
        return
      }

      const allDataKeys = new Set<string>()
      data.Loan.forEach((item: IReportHeaders) => item.data.forEach((d) => allDataKeys.add(d)))

      getLoanReport(id)
        .then(({ title, headers, filters, origin: reportOrigin, access: reportAccess, canEdit: reportCanEdit }) => {
          setTitle(title)
          const toRecord = (values?: string[]) => Object.fromEntries((values || []).map((key) => [key, true]))
          setAccess({
            viewAccountTypes: toRecord(reportAccess?.viewAccountTypes),
            viewUsers: toRecord(reportAccess?.viewUsers),
            editAccountTypes: toRecord(reportAccess?.editAccountTypes),
            editUsers: toRecord(reportAccess?.editUsers),
          })
          setCanEdit(reportCanEdit !== false)
          setSelectedPlaces(
            Array.from(
              new Set<string>(
                headers.map((header: string) =>
                  header === 'Borrow Application completed date' ? 'Borrower Application Completed Date' : header,
                ),
              ),
            ).filter((header) => allDataKeys.has(header) || allDataKeys.has(header.split('-')[0])),
          )
          // Older saved reports omit propertyType; default to every type so collapse() and the
          // MultiSelect stay valid and the report stays unfiltered until the user narrows it.
          setFilters({
            ...filters,
            propertyType: filters?.propertyType || generateOptions(loanOptionsConstants.propertyType),
          })
          if (reportOrigin !== undefined) setOrigin(reportOrigin)
        })
        .catch(() => {
          navigate.push(queryOrigin === LoanReportsOrigin.Servicing ? '/reports/servicing' : '/reports')
        })
        .finally(() => setLoading(false))
    }

    initData()
  }, [viewerKey])

  useEffect(() => {
    if (!canConfigureAccess) return
    getAdminToAeEmails().then((emails: string[]) => setUserEmails(Array.isArray(emails) ? emails : []))
  }, [canConfigureAccess])

  const onChangeFilter = useCallback((key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }))
    setHasUnsavedChanges(true)
  }, [])

  const onChangeAccess = useCallback((key: string, value: Record<string, boolean>) => {
    setAccess((prev) => ({ ...prev, [key]: value || {} }))
    setHasUnsavedChanges(true)
  }, [])

  const onSave = useCallback(() => {
    if (!canEdit) return
    setLoading(true)
    const payload: Record<string, any> = { type, title, headers: selectedPlaces, filters, origin }
    if (canConfigureAccess) {
      payload.access = {
        viewAccountTypes: filterObject(access.viewAccountTypes),
        viewUsers: filterObject(access.viewUsers),
        editAccountTypes: filterObject(access.editAccountTypes),
        editUsers: filterObject(access.editUsers),
      }
    }
    saveReports(id, payload)
      .then((response) => {
        setHasUnsavedChanges(false)
        const newReportId = response?.identifiers?.[0]?.id
        if (isNew && newReportId) navigate.replace(`/reports/${newReportId}`)
      })
      .finally(() => setLoading(false))
  }, [id, type, title, selectedPlaces, filters, origin, isNew, canEdit, canConfigureAccess, access])

  const getFilters = useCallback(
    (base: Record<string, any> = filters) => {
      const { hmdaLoanPurpose, ...rest } = base
      const collapse = (key: string) => {
        const arr = filterObject(base[key])
        return arr.length === filterOptionSizes[key as keyof typeof filterOptionSizes] ? [] : arr
      }
      const isSubStatusMode = base.statusMode === 'subStatus' && hasSubStatusOptions
      const selectedSubStatuses = isSubStatusMode ? filterObject(base.subStatus || {}) : []
      const parentStatusesOfSelectedSubStatuses = Array.from(
        new Set(selectedSubStatuses.map((v: string) => v.split(':')[0]).filter(Boolean)),
      )
      const purposeCodes = id === 'hmda' ? filterObject(hmdaLoanPurpose || {}) : []
      const columnValueFilters = Array.isArray(rest.columnValueFilters) ? [...rest.columnValueFilters] : []
      // Empty or every code means no extra restriction. Otherwise keep rows whose HMDA
      // Loan Purpose cell is one of the selected submission codes.
      if (purposeCodes.length && purposeCodes.length < Object.keys(hmdaPurposeOptions).length) {
        columnValueFilters.push({ column: 'Loan Purpose', values: purposeCodes })
      }
      return {
        ...rest,
        status: isSubStatusMode ? parentStatusesOfSelectedSubStatuses : collapse('status'),
        subStatus: selectedSubStatuses,
        lock: collapse('lock'),
        type: collapse('type'),
        productType: collapse('productType'),
        propertyType: collapse('propertyType'),
        state: collapse('state'),
        // Selected tags only (empty → no tag filter); backend filters loan.tags by these.
        tags: filterObject(base.tags || {}),
        // Loans carrying any of these are dropped, even when they match `tags`.
        excludeTags: filterObject(base.excludeTags || {}),
        ...(columnValueFilters.length ? { columnValueFilters } : {}),
      }
    },
    [filters, filterOptionSizes, hasSubStatusOptions, id, hmdaPurposeOptions],
  )

  const getSelectedDateColumns = useCallback(() => {
    const result: string[] = []
    selectedPlaces.forEach((header) => {
      if (dateHeadersSet.has(extractHeaderLabel(header))) result.push(header)
    })
    return result
  }, [selectedPlaces, dateHeadersSet])

  const buildFiltersWithDateRange = useCallback(
    (dateFiltersList?: DateFilterPayload[], filtersBase?: Record<string, any>) => {
      const computedFilters: Record<string, any> = { ...getFilters(filtersBase) }
      const validFilters = (dateFiltersList || []).filter((f) => f.column && (f.range.from || f.range.to))
      if (validFilters.length) {
        computedFilters.dateRangeFilters = validFilters.map((f) => ({
          column: f.column,
          from: f.range.from,
          to: f.range.to,
        }))
      }
      return computedFilters
    },
    [getFilters],
  )

  const runAction = useCallback(
    (action: ActionType, dateFiltersList?: DateFilterPayload[], filtersBase?: Record<string, any>) => {
      const payloadFilters = buildFiltersWithDateRange(dateFiltersList, filtersBase)
      const payload = {
        type,
        id,
        title,
        headers: selectedPlaces,
        filters: payloadFilters,
        requiredHeaders,
        // Which book to read. A saved report's own `origin` is authoritative server-side; sending it
        // is what lets a report that has not been saved yet preview against the right pipeline.
        origin,
      }

      setLoading(true)
      if (action === 'downloadPipe') {
        startHmdaPipeJob(payload)
          .then((res) => {
            if (!res?.jobId) {
              toast(res?.message || 'Could not start the HMDA pipe download.', { type: 'error' })
              return
            }
            trackHmdaPipeRun(res.jobId, 'HMDA pipe file', 'HMDA Report.txt')
            toast('Running in the background. You will be notified when it is ready.', { type: 'info' })
          })
          .catch((error: any) => toast(error?.message || 'Could not start the HMDA pipe download.', { type: 'error' }))
          .finally(() => setLoading(false))
        return
      }

      const promise =
        action === 'preview'
          ? actionApiMap.preview(payload).then(({ data, total, dateFilterScanLimit }) => {
              setPreview(data)
              setTotal(total)
              setDateFilterScanLimit(Number(dateFilterScanLimit) || 0)
              setQueryTimes((prev) => prev + 1)
            })
          : actionApiMap[action](payload)

      promise.finally(() => setLoading(false))
    },
    [type, id, title, selectedPlaces, requiredHeaders, buildFiltersWithDateRange, origin],
  )

  const openDateModal = useCallback(
    (action: ActionType, columns: string[]) => {
      setPendingAction(action)
      setDateModalColumns(columns)
      const savedArr: Array<{ column: string; from?: string; to?: string }> =
        filters.dateRangeFilters || (filters.dateRangeFilter?.column ? [filters.dateRangeFilter] : null)
      const defaultFilter: DateFilterPayload = { column: columns[0] || '', range: {} }
      if (savedArr?.length) {
        const restored = savedArr
          .filter((f: any) => columns.includes(f.column))
          .map((f: any) => ({ column: f.column, range: { from: f.from, to: f.to } }))
        setDateFilters(restored.length ? restored : [defaultFilter])
      } else {
        setDateFilters([defaultFilter])
      }
      setDateModalError('')
      setIsDateModalOpen(true)
    },
    [filters],
  )

  const handleActionRequest = useCallback(
    (action: ActionType) => {
      if (selectedPlaces.length === 0) return
      const matchedColumns = getSelectedDateColumns()
      if (matchedColumns.length === 0) {
        runAction(action)
        return
      }
      openDateModal(action, matchedColumns)
    },
    [selectedPlaces, getSelectedDateColumns, runAction, openDateModal],
  )

  const handleDateModalClose = useCallback(() => {
    setIsDateModalOpen(false)
    setDateModalError('')
    if (pendingAction) {
      const actionToRun = pendingAction
      setPendingAction(null)
      runAction(actionToRun)
    }
  }, [pendingAction, runAction])

  const handleDateModalOk = useCallback(() => {
    if (!pendingAction) {
      setIsDateModalOpen(false)
      return true
    }

    const activeFilters = dateFilters.filter((f) => f.column)
    const filtersWithDates = activeFilters.filter((f) => f.range.from || f.range.to)

    const actionToRun = pendingAction
    setPendingAction(null)
    setIsDateModalOpen(false)
    setDateModalError('')

    const newFilters: Record<string, any> = { ...filters }
    delete newFilters.dateRangeFilter
    delete newFilters.dateRangeFilters
    if (filtersWithDates.length > 0) {
      newFilters.dateRangeFilters = filtersWithDates.map((f) => ({
        column: f.column,
        from: f.range.from,
        to: f.range.to,
      }))
    }
    setFilters(newFilters)
    setHasUnsavedChanges(true)

    runAction(actionToRun, activeFilters.length > 0 ? activeFilters : undefined, newFilters)
    return true
  }, [pendingAction, dateFilters, filters, runAction])

  const onPlaceholderChange = useCallback((value: any) => {
    setSelectedPlaces(value)
    setHasUnsavedChanges(true)
  }, [])

  const onTitleChange = useCallback((value: string) => {
    setTitle(value)
    setHasUnsavedChanges(true)
  }, [])

  const onSortableChange = useCallback((values: any[]) => {
    setSelectedPlaces(values.map(({ data }) => data))
    setHasUnsavedChanges(true)
  }, [])

  useEffect(() => {
    setRequiredHeaders((prev) => prev.filter((header) => selectedPlaces.includes(header)))
  }, [selectedPlaces])

  // A "your file is ready" notification links here with the job id. Pull the file down and drop the
  // parameter so a reload does not download it again. Other query params (origin) stay.
  useEffect(() => {
    if (String(id) !== 'hmda') return
    const params = new URLSearchParams(location.search)
    const jobId = params.get('job')
    if (!jobId) return
    params.delete('job')
    const search = params.toString()
    navigate.replace({ search: search ? `?${search}` : '' })
    getHmdaPipeJob(jobId)
      .then(async (res) => {
        const job = res?.job
        if (job?.status === 'done') await downloadHmdaPipeJobFile(job.jobId, job.fileName)
        else toast(res?.message || 'That HMDA pipe file is no longer available to download.', { type: 'warning' })
      })
      .catch(() => toast('That HMDA pipe file is no longer available to download.', { type: 'warning' }))
  }, [id, location.search])

  const openRequiredModal = useCallback(
    (header: string) => {
      setRequiredModalHeader(header)
      setRequiredModalChecked(requiredHeaders.includes(header))
      setIsRequiredModalOpen(true)
    },
    [requiredHeaders],
  )

  const handleRequiredModalOk = useCallback(() => {
    setRequiredHeaders((prev) => {
      const next = prev.filter((header) => header !== requiredModalHeader)
      if (requiredModalChecked) next.push(requiredModalHeader)
      return next
    })
    setIsRequiredModalOpen(false)
    return true
  }, [requiredModalHeader, requiredModalChecked])

  const requiredHeaderSet = useMemo(() => new Set(requiredHeaders), [requiredHeaders])

  const statusModeToggle = hasSubStatusOptions ? (
    <button
      type="button"
      className="text-shade-blue hover:underline whitespace-nowrap font-normal normal-case"
      onClick={(e) => {
        e.stopPropagation()
        onChangeFilter('statusMode', filters.statusMode === 'subStatus' ? 'status' : 'subStatus')
      }}
    >
      {filters.statusMode === 'subStatus' ? 'Use Status' : 'Use Sub-Status'}
    </button>
  ) : null

  const previewHeaders = preview[0]
  const previewRows = useMemo(() => preview.slice(1), [preview])
  const filteredPreviewRows = useMemo(() => {
    if (!previewHeaders || requiredHeaders.length === 0) return previewRows
    const indexes = requiredHeaders.map((header) => previewHeaders.indexOf(header)).filter((index) => index >= 0)
    if (!indexes.length) return previewRows
    return previewRows.filter((row) => indexes.every((index) => hasPreviewCellValue(row[index])))
  }, [previewRows, previewHeaders, requiredHeaders])
  const previewTotal = requiredHeaders.length ? filteredPreviewRows.length : total

  return (
    <div className="Reports-container py-6 px-2">
      <div className="relative shadow1 max-w-screen-2xl m-auto bg-white rounded p-3 md:p-7 sm:text-center lg:text-left w-full">
        <LayoutLoading show={loading} />
        <div className="relative">
          <div className="text-shade-blue flex items-center mb-2 font-bold">
            <ChevronLeftIcon className="w-4 h-4" />
            <Link
              to={origin === LoanReportsOrigin.Servicing ? '/reports/servicing' : '/reports'}
              className="hover:underline"
            >
              Return to Reports
            </Link>
          </div>

          <div className="flex flex-wrap justify-between">
            <h1 className="text-2xl font-bold flex items-center mb-2">
              <span>{isNew ? 'Create Report' : title}</span>
              {loading && (
                <span className="text-base ml-3">
                  <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
                </span>
              )}
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ReportPlaceholders
            placeholders={placeholders}
            selectedPlaces={selectedPlaces}
            setSelectedPlaces={onPlaceholderChange}
            type={type}
            setType={setType}
          />

          <div className="md:col-span-2">
            {!isStandard && (
              <div className="border-b border-gray-100 pb-4 mb-4">
                <div className="grid items-center grid-cols-2 md:grid-cols-3 gap-4">
                  <Input2 type="text" title="Title" required value={title} onChange={onTitleChange} />
                  <div>
                    <Button disabled={!canEdit || !title || !hasUnsavedChanges} onClick={onSave}>
                      Save
                    </Button>
                  </div>
                </div>
                {!canEdit && (
                  <p className="text-sm text-gray-500 mt-2">
                    You can run this report. Saving changes is limited to the people selected to edit it.
                  </p>
                )}
              </div>
            )}

            {!isStandard && canConfigureAccess && (
              <div className="border-b border-gray-100 pb-4 mb-4">
                <p className="text-lg font-bold mb-1">Who can see and change this report</p>
                <p className="text-sm text-gray-500 mb-3">
                  Leave a list empty to keep it open. An empty view list means anyone who can open Reports can see it.
                  An empty edit list means anyone who can see it can change it. Who may set these lists is granted under
                  Admin &gt; Permissions, in the Reports group, as Access.
                </p>
                <div className="grid items-center grid-cols-1 md:grid-cols-2 gap-4">
                  <MultiSelect
                    id="report-view-profiles"
                    title="Profiles who can view"
                    options={accountTypes}
                    value={access.viewAccountTypes}
                    defaultSelected={false}
                    sort
                    onChange={(value) => onChangeAccess('viewAccountTypes', value)}
                  />
                  <MultiSelect
                    id="report-view-users"
                    title="Users who can view"
                    options={userOptions}
                    value={access.viewUsers}
                    defaultSelected={false}
                    sort
                    onChange={(value) => onChangeAccess('viewUsers', value)}
                  />
                  <MultiSelect
                    id="report-edit-profiles"
                    title="Profiles who can change"
                    options={accountTypes}
                    value={access.editAccountTypes}
                    defaultSelected={false}
                    sort
                    onChange={(value) => onChangeAccess('editAccountTypes', value)}
                  />
                  <MultiSelect
                    id="report-edit-users"
                    title="Users who can change"
                    options={userOptions}
                    value={access.editUsers}
                    defaultSelected={false}
                    sort
                    onChange={(value) => onChangeAccess('editUsers', value)}
                  />
                </div>
              </div>
            )}

            <div className="border-b border-gray-100 pb-4 mb-4">
              <p className="text-lg font-bold mb-2">Filters</p>

              <div className="grid items-center grid-cols-2 md:grid-cols-3 gap-4">
                <Input2
                  type="search"
                  title="Search Loans"
                  hasIcon
                  icon={svgSearch}
                  value={filters.query}
                  onChange={(value) => onChangeFilter('query', value)}
                />
                {filters.statusMode === 'subStatus' && hasSubStatusOptions ? (
                  <MultiSelect
                    id="loan-sub-status"
                    title="Loan Sub-Status"
                    options={subStatusOptions}
                    value={filters.subStatus || {}}
                    defaultSelected={false}
                    additionalElements={statusModeToggle}
                    onChange={(value) => onChangeFilter('subStatus', value)}
                  />
                ) : (
                  <MultiSelect
                    id="loan-status"
                    title="Loan Status"
                    options={fullLoanStatusOptions}
                    value={filters.status}
                    defaultSelected={false}
                    additionalElements={statusModeToggle}
                    onChange={(value) => onChangeFilter('status', value)}
                  />
                )}
                <MultiSelect
                  id="loan-type"
                  title="Channel"
                  options={channelTypeOptions}
                  value={filters.type}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('type', value)}
                />
                <MultiSelect
                  id="lock-status"
                  title="Lock"
                  options={lockTypeOptions}
                  value={filters.lock}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('lock', value)}
                />
                <MultiSelect
                  id="product-type"
                  title="Product Type"
                  options={productTypeOptionsMap}
                  value={filters.productType}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('productType', value)}
                />
                <MultiSelect
                  id="state"
                  title="State"
                  options={loanOptionsConstants.states}
                  value={filters.state}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('state', value)}
                />
                <MultiSelect
                  id="tags"
                  title="Include Tags"
                  options={tagOptions}
                  value={filters.tags}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('tags', value)}
                />
                <MultiSelect
                  id="exclude-tags"
                  title="Exclude Tags"
                  options={tagOptions}
                  value={filters.excludeTags}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('excludeTags', value)}
                />
                <InputDateRange
                  title="As of Date"
                  value={filters.asOf}
                  onChange={(value) => onChangeFilter('asOf', value)}
                />
                {/*
                  Same default-rate slices the servicing pipeline offers, so a report and the grid
                  scope the book identically (both go through LoanService.find()).
                */}
                <Select2
                  id="default-rate-status"
                  title="Default Rate"
                  options={defaultRateFilterOptions}
                  value={filters.defaultRate}
                  hasDefaultOption
                  defaultOptionText="All"
                  onChange={(value) => onChangeFilter('defaultRate', value)}
                />
                {/* Same Hold slice the servicing pipeline offers, for the same reason. */}
                <Select2
                  id="servicing-hold-status"
                  title="Hold"
                  options={holdFilterOptions}
                  value={filters.hold}
                  hasDefaultOption
                  defaultOptionText="All"
                  onChange={(value) => onChangeFilter('hold', value)}
                />
                <MultiSelect
                  id="property-type"
                  title="Property Type"
                  options={loanOptionsConstants.propertyType}
                  value={filters.propertyType}
                  defaultSelected={false}
                  onChange={(value) => onChangeFilter('propertyType', value)}
                />
                {id === 'hmda' && (
                  <MultiSelect
                    id="hmda-loan-purpose"
                    title="Loan Purpose"
                    options={hmdaPurposeOptions}
                    value={filters.hmdaLoanPurpose || {}}
                    defaultSelected={false}
                    onChange={(value) => onChangeFilter('hmdaLoanPurpose', value)}
                  />
                )}
                {id === 'hmda' && (
                  <InputDateRange
                    title="Last Status Change Date"
                    value={filters.lastStatusChange}
                    onChange={(value) => onChangeFilter('lastStatusChange', value)}
                  />
                )}
              </div>
            </div>

            <div className="border-b border-gray-100 pb-4 mb-4">
              <p className="text-lg font-bold mb-2">Headers</p>
              <p className="text-xs text-gray-500 mb-2">Double-click a header to mark it as required.</p>
              <div className="flex flex-wrap gap-1">
                <Sortable
                  data={selectedPlaces.map((v, idx) => ({ id: `${v}-${idx}`, data: v }))}
                  setData={onSortableChange}
                  renderItem={(data: any, index: number) => {
                    const isRequired = requiredHeaderSet.has(data)
                    return (
                      <p
                        className={`px-2 py-1 border rounded-md text-sm cursor-pointer ${
                          isRequired ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-200'
                        }`}
                        title="Double-click to set required"
                        onDoubleClick={(e) => {
                          e.stopPropagation()
                          openRequiredModal(data)
                        }}
                      >
                        {index + 1}. {resolveDisplayName(data)}
                        {isRequired ? ' *' : ''}
                      </p>
                    )
                  }}
                />
              </div>
            </div>

            {dateFilterScanLimit > 0 && (
              <div className="mt-2 text-sm text-amber-700">
                The date range was checked against the first {dateFilterScanLimit.toLocaleString()} loans only. Narrow
                the other filters to cover them all, or download the report, which covers every loan.
              </div>
            )}

            <div className="flex items-center gap-4 justify-between mb-2">
              <p className="text-lg font-bold">Preview ({previewTotal})</p>
              <div className="flex items-center gap-4">
                <p
                  className="text-sm cursor-pointer text-shade-blue hover:underline"
                  onClick={() => handleActionRequest('preview')}
                >
                  Preview
                </p>
                <span
                  className="p-1 text-sm cursor-pointer text-shade-blue hover-shadow1 rounded"
                  onClick={() => handleActionRequest('download')}
                >
                  <Tooltip message="Download Excel">
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </Tooltip>
                </span>
                <span
                  className="p-1 text-sm cursor-pointer text-shade-blue hover-shadow1 rounded"
                  onClick={() => handleActionRequest('downloadCSV')}
                >
                  <Tooltip message="Download CSV">
                    <ArrowDownTrayIcon className="w-4 h-4" />
                  </Tooltip>
                </span>
                {id === 'hmda' && (
                  <span
                    className="p-1 text-sm cursor-pointer text-shade-blue hover-shadow1 rounded inline-flex items-center gap-1"
                    onClick={() => handleActionRequest('downloadPipe')}
                  >
                    <Tooltip message="Download Pipe">
                      <span className="inline-flex items-center gap-1">
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        Pipe
                      </span>
                    </Tooltip>
                  </span>
                )}
              </div>
            </div>

            {previewHeaders && (
              <HorzScrollContainer size={queryTimes}>
                <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6 overflow-auto">
                  <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th className="px-2 py-1">No</th>
                      {previewHeaders.map((key, i) => (
                        <th key={i} className="px-2 py-1">
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="text-[14px] text-gray-900">
                    {filteredPreviewRows.map((item, index) => (
                      <tr key={index} className={`border ${index % 2 !== 0 ? 'bg-gray-50' : ''}`}>
                        <td className="px-2 py-2">{index + 1}</td>
                        {item.map((v, ci) => (
                          <td key={ci} className="px-2 py-2 whitespace-nowrap">
                            {v}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </HorzScrollContainer>
            )}
          </div>
        </div>
      </div>

      {isDateModalOpen && (
        <Modal
          title="Date Range Filters"
          isOpen
          titleOkay="Apply"
          titleCancel="Skip"
          onOk={handleDateModalOk}
          onClose={handleDateModalClose}
        >
          <div className="w-[550px] space-y-4">
            <p className="text-sm text-gray-700">
              Optionally set date ranges for the columns below. Apply without dates to run the report with no date
              filters.
            </p>
            {dateFilters.map((filter, index) => {
              const usedColumns = new Set(
                dateFilters
                  .filter((_, i) => i !== index)
                  .map((f) => f.column)
                  .filter(Boolean),
              )
              const availableColumns = dateModalColumns.filter((c) => !usedColumns.has(c))
              return (
                <div key={index} className="border rounded p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <RenderInput
                        input={{
                          inputType: 'Select',
                          title: `Date Column${dateFilters.length > 1 ? ` ${index + 1}` : ''}`,
                          options: availableColumns,
                          value: filter.column,
                          hasDefaultOption: true,
                        }}
                        Key={`date-column-${index}`}
                        onChange={(_key: string, value: string) => {
                          const updated = [...dateFilters]
                          updated[index] = { ...updated[index], column: value }
                          setDateFilters(updated)
                          setDateModalError('')
                        }}
                      />
                    </div>
                    {dateFilters.length > 1 && (
                      <span
                        className="text-red-500 cursor-pointer hover-shadow1 p-1 rounded mt-5"
                        onClick={() => {
                          setDateFilters(dateFilters.filter((_, i) => i !== index))
                          setDateModalError('')
                        }}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </span>
                    )}
                  </div>
                  {filter.column && (
                    <InputDateRange
                      title="Date Range"
                      name={`date-range-${index}`}
                      value={filter.range}
                      onChange={(value: { from?: string; to?: string }) => {
                        const updated = [...dateFilters]
                        updated[index] = { ...updated[index], range: value }
                        setDateFilters(updated)
                        setDateModalError('')
                      }}
                    />
                  )}
                </div>
              )
            })}
            {dateFilters.length < dateModalColumns.length && (
              <p
                className="text-shade-blue text-sm cursor-pointer hover:underline"
                onClick={() => {
                  setDateFilters([...dateFilters, { column: '', range: {} }])
                  setDateModalError('')
                }}
              >
                + Add Date Filter
              </p>
            )}
            {dateModalError && <p className="text-sm text-rose-600">{dateModalError}</p>}
          </div>
        </Modal>
      )}

      {isRequiredModalOpen && (
        <Modal
          title={resolveDisplayName(requiredModalHeader)}
          isOpen
          titleOkay="Save"
          onOk={handleRequiredModalOk}
          onClose={() => setIsRequiredModalOpen(false)}
        >
          <div className="w-[360px]">
            <p className="text-sm text-gray-700 mb-3">
              If required is checked, preview will only include loans that have a value for this header.
            </p>
            <Checkbox
              id="report-header-required"
              title="Required"
              value={requiredModalChecked}
              onChange={(checked) => setRequiredModalChecked(checked)}
            />
          </div>
        </Modal>
      )}
    </div>
  )
}

export default ReportDetails
